import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel
} from '@chakra-ui/form-control'
import { Input } from '@chakra-ui/input'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Icon,
  Link,
  Text
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { RiDeleteBinFill } from 'react-icons/ri'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type {
  DeleteAccountErrorResponse,
  UserSessionResponse
} from '../../interfaces'
import PasswordInput from '../PasswordInput'
import SetPasswordAlert from '../SetPasswordAlert'

interface Props {
  isOpen: boolean
  user: UserSessionResponse | null
  onClose(): void
}

interface FormProps {
  userEmail: string
  userPassword: string
}

const DeleteAccountModal = ({
  isOpen,
  onClose,
  user,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const [formError, setFormError] = useState<string>('')

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <Formik
          initialValues={{
            userEmail: user?.userEmail,
            userPassword: '',
          }}
          validationSchema={Yup.object().shape({
            userEmail: Yup.string()
              .email('Must be a valid email')
              .required('Required'),
            userPassword: Yup.string()
              .min(1, 'Should be of minimum length 1')
              .required('Required'),
          })}
          onSubmit={async (values): Promise<void> => {
            setFormError('')
            if (user?.clientNumber) {
              const response = (await request.delete(
                `/api/account/${user.clientNumber}`,
                values,
              )) as DeleteAccountErrorResponse | null

              if (request.error) {
                setFormError(
                  response?.message ??
                    t(
                      'error_in_deleting_account',
                      'Error in deleting account.',
                    ),
                )
              } else {
                toast({
                  description: t(
                    'account_delete_success_message',
                    'Account has been deleted, you will be shortly redirected to home page.',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                onClose()
                setFormError('')
                setTimeout(() => {
                  window.location.href = '/'
                }, 3000)
              }
            }
          }}
        >
          {({ isSubmitting, submitForm, isValid, dirty }) => (
            <>
              <ModalHeader>
                <Icon
                  as={RiDeleteBinFill}
                  color={colors.appBattleshipGrey}
                  backgroundColor={colors.appPaleGrey}
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="50%"
                  marginRight="0.5em"
                />
                {t('delete_account', 'Delete Account')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {!user?.isPasswordSetByUser ? (
                  <SetPasswordAlert onLinkClick={onClose} />
                ) : null}
                <Alert status="error">
                  <AlertIcon />
                  <AlertDescription>
                    <Trans t={t} i18nKey="delete_account_warning">
                      This action is irreversible. It will delete all associated
                      users, uploaded media files, URL-endpoint and origin
                      settings, and any other data associated with the account.
                    </Trans>
                  </AlertDescription>
                </Alert>
                {formError ? (
                  <Alert status="error" mt="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Text m="1em 0em">
                  {t(
                    'enter_password_to_continue',
                    'To continue, please enter your account password.',
                  )}
                </Text>
                <Field name="userEmail">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="0.3em"
                      isInvalid={Boolean(
                        form.errors.userEmail && form.touched.userEmail,
                      )}
                      isReadOnly
                      isDisabled
                      isRequired
                    >
                      <FormLabel>{t('email', 'Email')}</FormLabel>
                      <Input {...field} placeholder={t('email', 'Email')} />
                      {form.errors.userEmail && form.touched.userEmail ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.userEmail}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
                <Field name="userPassword">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="0.3em"
                      isInvalid={Boolean(
                        form.errors.userPassword && form.touched.userPassword,
                      )}
                      isRequired
                    >
                      <FormLabel>{t('password', 'Password')}</FormLabel>
                      <PasswordInput
                        field={field}
                        isDisabled={!user?.isPasswordSetByUser}
                        isPasswordSetByUser={user?.isPasswordSetByUser ?? false}
                        showToggle={true}
                      />
                      {form.errors.userPassword && form.touched.userPassword ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.userPassword}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  colorScheme="appRed"
                  isDisabled={!isValid || !dirty}
                  isLoading={isSubmitting}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default DeleteAccountModal
